import { inject, injectionTarget } from '../../../loader/decorators/inject.js';
import { ADSLOT_REGISTRY, CONFIG_RESOLVER } from '../../../loader/token.js';
import { ConfigResolver } from '../../../loader/essentials/config-resolver.js';
import { onPhase, Phase } from '../../../loader/decorators/on-phase.js';
import { selectModuleSettings, selectPositionSettings } from '../../../loader/essentials/config-selector.js';
import { SlotEventDetail, SystemSlotEvent } from '@mbrtargeting/metatag-shared-types';
import { getIdsForAdSlot } from '../../utils/adslot-data-store.js';
import { isSelectorValid, once } from '@mbrtargeting/metatag-utils';
import { AdslotRegistry } from '../../../loader/modules/adslot-registry.js';
import { logger } from '../../../loader/essentials/logger.js';

const log = logger.logGroup({ prefix: 'SecondarySkyscraper' });

interface IntegrateSecondarySkyscraperOptions {
    anchorQuery: string;
    cssClasses: string[];
}


/**
 * SecondarySkyscraper will integrate a new skyscraper right of the publishers content.
 * It should only serve on specific buyers/campaigns values originating from stroeerCore and only if the website has sufficient height to allow 2 skyscrapers to show.
 * Relates: https://stroeerdigitalgroup.atlassian.net/browse/TICKET-42951
 */
@injectionTarget()
export class SecondarySkyscraper {

    @inject(CONFIG_RESOLVER) #configResolver!: ConfigResolver;
    @inject(ADSLOT_REGISTRY) private adslotRegistry!: AdslotRegistry;


    //create slot "sky3" only in certain cases and make sure to only do it once per page impression
    private integrateSecondarySky = once((options: IntegrateSecondarySkyscraperOptions) => {
        const { anchorQuery, cssClasses } = options;
        const sky3Container = document.createElement('div');

        sky3Container.id = 'sdg-created-slot-sky3';
        sky3Container.classList.add(...cssClasses);

        document.querySelector(anchorQuery)?.appendChild(sky3Container);

        //update config of "sky", so it will not collide with "sky3" when being sticky
        this.#configResolver.update(selectPositionSettings('sky'), (skyConfig) => {
            skyConfig.stickyStopQuery = `div#${sky3Container.id}`;
            return skyConfig;
        });

        //add adslot "sky3"
        const sky3 = this.adslotRegistry.createAdslot('sky3', sky3Container);

        //creating sky3 might have caused errors
        if (!sky3) return log.error('sky3 not created, discontinue SecondSkyscraper module');

        sky3.load();
    });


    @onPhase({ phase: Phase.DELIVERY })
    public async init(): Promise<void> {
        const {
            active = false,
            minSiteHeight = 8000,
            onCampaignId = [],
            onLastSsp = [],
            onBuyerName = [],
            anchorQuery = '',
            cssClasses = [],
            forceSky = false,
        } = this.#configResolver.get(selectModuleSettings('SECONDARY_SKYSCRAPER')) || {};

        if (!active) return;

        if (!isSelectorValid(anchorQuery)) return log.error('querySelector for attaching sky3 invalid.');

        //check website height only once, we do not care about later changes
        const websiteHeight = Math.max(document.documentElement.scrollHeight, document.documentElement.offsetHeight, document.body.scrollHeight);

        //website should be larger than configured minimum height
        if (websiteHeight < minSiteHeight) return log.debug('websiteHeight insufficient to serve sky3.');

        log.debug('websiteHeight allows sky3.');

        //when adslot "sky" is done, we should have full set of IDs to decide if "sky3" can be integrated
        const waitForSkyDone = ({ detail }: CustomEvent<SlotEventDetail>) => {
            const { slotName } = detail;

            //watch only sky
            if (slotName !== 'sky') return;

            //only allow when delivering SSP is matching against config AND if either a matching campaignId OR matching buyerName was delivered on "sky"
            const { lastSsp, campaignId, buyerName } = getIdsForAdSlot(slotName);

            /**
             * sky should only deliver when a matching SSP is delivering AND either a specific campaignId or BuyerName is present
             * Alternatively sky can be forced.
             */
            if (
                (
                    onLastSsp.some(value => value === lastSsp)
                    &&
                    (onCampaignId.some(value => value.toString() === campaignId) || onBuyerName.some(value => value === buyerName))
                ) ||
                (
                    forceSky
                )
            ) {
                log.debug('sky3 can serve. LastSsp: %s, campaignId: %s, buyerName: %s, forced: %s', [lastSsp, campaignId, buyerName, forceSky]);
                window.removeEventListener(SystemSlotEvent.SDG_SLOT_DONE, waitForSkyDone);
                this.integrateSecondarySky({
                    anchorQuery,
                    cssClasses,
                });
            }
        }

        window.addEventListener(SystemSlotEvent.SDG_SLOT_DONE, waitForSkyDone);
    }
}